'use client';

import { useMemo } from 'react';
import {
  createEbvValueFormatter,
  EBV_SORT_ORDER,
  formatEbvAccuracyDisplay,
  getEbvCategoryRanker,
  compareEbvByCategoryOrder,
  getEbvDisplayCategory,
  ebvGroupingOptionsFromDisplay,
  parseEbvAverage,
  parseEbvConfig,
} from '@/hooks/publicSearch/ebv/ebvColumns';
import {
  resolveBrBandPrefix,
  resolveTraitPercentileNumber,
  type EbvPercentageBandRow,
} from '@/components/publicSearch/animal/ebv/calculateTopPercent';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import {
  coerceBreedAverageRaw,
  formatBreedAverageDisplay,
} from '@/components/publicSearch/animal/ebv/breedAverageDisplay';
import type { EbvChartItem } from '@/components/publicSearch/animal/ebv/EbvPercentileGraph';
import { getEbvConfigTraitKey } from '@/components/publicSearch/animal/ebv/ebvTraitExplanations';
import { resolveEbvShortLabel } from '@/components/publicSearch/animal/ebv/ebvShortLabel';
import { isTraitLeaderForEbvField } from '@/components/publicSearch/animal/ebv/traitLeader';
import type { Society } from '@/publicSearch/store/usePublicSearchContext';
import { useCurrencySymbol } from '@/hooks/publicSearch/useCurrencySymbol';
import { useAnimalView } from '@/hooks/publicSearch/animal/useAnimalView';

/** Animal record (carrying `ebvRaw`) as returned by {@link useAnimalView}. */
export type AnimalEbvSource = ReturnType<typeof useAnimalView>['animalData'];

/** Raw EBV numeric data keyed by field name (e.g. `ebv1`, `ebv1Accuracy`, `ebv_1_avg`). */
type EbvRawData = Record<string, unknown>;

/**
 * Derives every EBV table/chart dataset for the animal-detail EBV tab from the
 * society settings (EBV_FIELDS / EBV_AVERAGE / EBV_DESCRIPTION) and the animal's
 * raw EBV values. Extracted from the animal page so the page stays focused on
 * layout and tab orchestration; the heavy data-shaping lives here.
 */
export function useAnimalEbvData(
  society: Society,
  animalData: AnimalEbvSource,
) {
  const ebvConfig = useMemo(
    () => parseEbvConfig(society.settings),
    [society.settings],
  );

  const currencySymbol = useCurrencySymbol(society);

  const ebvAverageData = useMemo(
    () => parseEbvAverage(society.settings),
    [society.settings],
  );

  const ebvAverageMeta = useMemo((): {
    runLabel: string;
    avgsCaption: string;
    avgCaptionB?: string;
    avgCaptionR?: string;
    medianCaption?: string;
    graphFooterCaption?: string;
  } => {
    return {
      runLabel: ebvAverageData?.runLabel?.trim() ?? '',
      avgsCaption: ebvAverageData?.avgsCaption?.trim() ?? '',
      avgCaptionB: ebvAverageData?.avgCaptionB?.trim() || undefined,
      avgCaptionR: ebvAverageData?.avgCaptionR?.trim() || undefined,
      medianCaption: ebvAverageData?.medianCaption?.trim() || undefined,
      graphFooterCaption:
        ebvAverageData?.graphFooterCaption?.trim() || undefined,
    };
  }, [ebvAverageData]);

  const { showTopPercentRow, isBlackRedAngus, useColourSlotGrouping } =
    useEbvDisplayOptions();

  const brPercentileBands = useMemo((): EbvPercentageBandRow[] => {
    const setting = society.settings?.find(
      (s) => s?.societySettingType === SOCIETY_SETTING_TYPE.EBV_PERCENTAGES_BR,
    );
    if (!setting?.value) return [];
    try {
      const parsed = JSON.parse(setting.value) as EbvPercentageBandRow[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [society.settings]);

  const ebvGroupingOptions = ebvGroupingOptionsFromDisplay({
    useColourSlotGrouping,
  });

  const ebvSortMode = useMemo(() => {
    const chars = Array.from({ length: 26 }, () => 'A');
    for (const field of ebvConfig) {
      if (!field?.field?.startsWith('ebv')) continue;
      const n = Number(field.field.replace('ebv', ''));
      if (n >= 1 && n <= 26) {
        chars[n - 1] = field.sortOrder === EBV_SORT_ORDER.DESC ? 'D' : 'A';
      }
    }
    return chars.join('');
  }, [ebvConfig]);

  const topPercentBandPrefix = resolveBrBandPrefix(
    animalData?.ebvRaw?.ebvDescription as string | undefined,
  );

  const percentileOptions = useMemo(
    () => ({
      showTopPercentRow,
      brPercentileBands,
      topPercentBandPrefix,
      ebvSortMode,
    }),
    [showTopPercentRow, brPercentileBands, topPercentBandPrefix, ebvSortMode],
  );

  const ebvRows = useMemo(() => {
    const raw = animalData?.ebvRaw as EbvRawData | undefined;
    if (!raw) return [];
    // Only include EBVs with display === 1 (display === 2 are hidden here)
    const visible = ebvConfig.filter(
      (ebvField) =>
        ebvField &&
        ebvField.display === 1 &&
        ebvField.field?.startsWith('ebv') &&
        ebvField.label,
    );
    const traitLeader =
      typeof raw.traitLeader === 'string' ? raw.traitLeader : '';

    const mapped = visible.map((ebvField) => {
      const fieldNumber = ebvField.field.replace('ebv', '');
      const fieldIndex = Number(fieldNumber);
      const valueKey = `ebv${fieldIndex}`;
      const accKey = `ebv${fieldIndex}Accuracy`;
      const avgKey = `ebv_${fieldIndex}_avg`;
      const formatValue = createEbvValueFormatter(
        ebvField.format || '0',
        currencySymbol,
      );
      const rawValue = raw[valueKey];
      const percentileNumber = resolveTraitPercentileNumber(
        raw,
        fieldIndex,
        percentileOptions,
      );
      const percentile =
        percentileNumber !== null && percentileNumber !== undefined
          ? String(percentileNumber)
          : '';
      return {
        key: ebvField.field,
        label: ebvField.label,
        shortLabel: resolveEbvShortLabel(ebvField.label),
        traitKey: getEbvConfigTraitKey(ebvField),
        value: formatValue(rawValue as string | null | undefined),
        avgKey,
        format: ebvField.format || '0',
        accuracy: formatEbvAccuracyDisplay(raw[accKey], {
          isBlackRedAngusStrain: isBlackRedAngus,
        }),
        percentile,
        isTraitLeader: isTraitLeaderForEbvField(traitLeader, fieldIndex),
        category: getEbvDisplayCategory(ebvField, ebvGroupingOptions),
        categoryOrder: ebvField.categoryOrder,
      };
    });

    const rankCategory = getEbvCategoryRanker(ebvConfig, ebvGroupingOptions);
    return mapped.sort((a, b) =>
      compareEbvByCategoryOrder(
        a.category,
        a.categoryOrder,
        b.category,
        b.categoryOrder,
        rankCategory,
      ),
    );
  }, [
    animalData,
    ebvConfig,
    currencySymbol,
    ebvGroupingOptions,
    percentileOptions,
  ]);

  const selectionIndexColumns = useMemo(() => {
    const raw = animalData?.ebvRaw as EbvRawData | undefined;
    if (!raw) return [];

    // Selection index columns come from EBV_FIELDS where display === 2
    const cols = ebvConfig.filter(
      (ebvField) =>
        ebvField &&
        ebvField.display === 2 &&
        ebvField.field?.startsWith('ebv') &&
        ebvField.label,
    );

    return cols.map((ebvField) => {
      const fieldNumber = ebvField.field.replace('ebv', '');
      const fieldIndex = Number(fieldNumber);
      const valueKey = `ebv${fieldIndex}`;
      const pctKey = `ebv${fieldIndex}Percentile`;
      const avgKey = `ebv_${fieldIndex}_avg`;
      const formatValue = createEbvValueFormatter(
        ebvField.format || '0',
        currencySymbol,
      );

      return {
        key: ebvField.field,
        label: ebvField.label,
        shortLabel: resolveEbvShortLabel(ebvField.label),
        traitKey: getEbvConfigTraitKey(ebvField),
        ebvValue: formatValue(raw[valueKey] as string | null | undefined),
        avgKey,
        format: ebvField.format || '0',
        breedAvg:
          ebvAverageData && ebvAverageData[avgKey] !== undefined
            ? formatBreedAverageDisplay(
                coerceBreedAverageRaw(ebvAverageData[avgKey]),
                ebvField.format || '0',
                currencySymbol,
              )
            : '',
        percentile:
          raw[pctKey] !== null && raw[pctKey] !== undefined
            ? String(raw[pctKey])
            : '',
      };
    });
  }, [animalData, ebvAverageData, ebvConfig, currencySymbol]);

  // Chart traits: trait EBVs (display === 1) plus selection indexes (display === 2).
  // Selection indexes are also listed in the index table above; here they feed the
  // percentile graph as "Indexes" bars. The potential-change graph still drops the
  // Indexes category, so they only surface on the percentile graph.
  const ebvChartData = useMemo((): EbvChartItem[] => {
    const raw = animalData?.ebvRaw as EbvRawData | undefined;
    if (!raw) return [];

    const visible = ebvConfig.filter(
      (ebvField) =>
        ebvField &&
        (ebvField.display === 1 || ebvField.display === 2) &&
        ebvField.field?.startsWith('ebv') &&
        ebvField.label,
    );

    const items = visible.map((ebvField) => {
      const fieldNumber = ebvField.field.replace('ebv', '');
      const fieldIndex = Number(fieldNumber);
      const valueKey = `ebv${fieldIndex}`;
      const accKey = `ebv${fieldIndex}Accuracy`;
      const avgKey = `ebv_${fieldIndex}_avg`;
      const seKey = `ebv_${fieldIndex}_se`;
      const formatValue = createEbvValueFormatter(
        ebvField.format || '0',
        currencySymbol,
      );

      const percentile = resolveTraitPercentileNumber(
        raw,
        fieldIndex,
        percentileOptions,
      );

      return {
        field: ebvField.field,
        label: ebvField.label.replace(/\^/g, ' '),
        leftLabel: ebvField.leftLabel || ebvField.label.replace(/\^/g, ' '),
        rightLabel: ebvField.rightLabel || '',
        value: formatValue(raw[valueKey] as string | null | undefined),
        accuracy: formatEbvAccuracyDisplay(raw[accKey], {
          isBlackRedAngusStrain: isBlackRedAngus,
        }),
        percentile,
        breedAvg:
          ebvAverageData && ebvAverageData[avgKey] !== undefined
            ? String(ebvAverageData[avgKey] ?? '')
            : '',
        formattedBreedAvg:
          ebvAverageData && ebvAverageData[avgKey] !== undefined
            ? formatBreedAverageDisplay(
                coerceBreedAverageRaw(ebvAverageData[avgKey]),
                ebvField.format || '0',
                currencySymbol,
              )
            : '',
        colour: ebvField.colour || 'B',
        chartColour: ebvField.chartColour,
        sortOrder: ebvField.sortOrder || EBV_SORT_ORDER.DESC,
        standardError:
          ebvAverageData && ebvAverageData[seKey] !== undefined
            ? String(ebvAverageData[seKey] ?? '')
            : '',
        lowValue: ebvField.lowValue || '',
        topValue: ebvField.topValue || '',
        category: getEbvDisplayCategory(ebvField, ebvGroupingOptions),
        categoryOrder: ebvField.categoryOrder,
        traitKey: ebvField.traitKey ?? ebvField.TraitKey,
      };
    });

    const rankCategory = getEbvCategoryRanker(ebvConfig, ebvGroupingOptions);
    return items.sort((a, b) =>
      compareEbvByCategoryOrder(
        a.category,
        a.categoryOrder,
        b.category,
        b.categoryOrder,
        rankCategory,
      ),
    );
  }, [
    animalData,
    ebvAverageData,
    ebvConfig,
    currencySymbol,
    ebvGroupingOptions,
    percentileOptions,
  ]);

  return {
    ebvAverageData,
    ebvAverageMeta,
    ebvRows,
    selectionIndexColumns,
    ebvChartData,
  };
}
